// Leaderboard flyout — Today / Weekly / All-Time boards fetched on open from
// the nopriv endpoints in leaderboard-functions.php. Loaded on every page
// (footer.php); the flyout shell lives in header.php and slides from the left
// like #menu / #archive-flyout. global.js's drawer controller dispatches
// 'lexagon:lb-open' when the drawer opens (toggle click or the endscreen
// teaser via window.openLeaderboardFlyout).
//
// Exposes window.LexagonBoards = { fetchBoard, flagImg } — endOfGame uses
// fetchBoard for the endscreen percentile + teaser.

(function () {
    'use strict';

    const flyout = document.getElementById('leaderboard-flyout');
    const AJAX_URL = '/wp-admin/admin-ajax.php';
    const CACHE_MS = 5 * 60 * 1000; // matches the server transients
    const cache = {};

    // 'puzzle' = the board for the puzzle being played, which is today's only
    // when you're on today's puzzle. The endpoint keeps its original name and
    // takes puzzle_post_id, falling back to today when none is sent.
    const ACTIONS = {
        puzzle: 'lexagon_leaderboard_today',
        weekly: 'lexagon_leaderboard_weekly',
        alltime: 'lexagon_leaderboard_alltime',
    };

    // Which puzzle the 'puzzle' tab is about. Undefined off a board page (the
    // flyout ships in the header everywhere) → the server falls back to today.
    function boardPostId(tab) {
        return tab === 'puzzle' ? (window.puzzlePostId || 0) : 0;
    }

    function escapeHTML(str) {
        const div = document.createElement('div');
        div.textContent = str ?? '';
        return div.innerHTML;
    }

    function flagImg(cc) {
        if (!cc || !/^[a-z]{2}$/i.test(cc)) return '';
        cc = cc.toLowerCase();
        return `<img class="lb-flag-img" src="https://flagcdn.com/24x18/${cc}.png"` +
            ` srcset="https://flagcdn.com/48x36/${cc}.png 2x" width="20" loading="lazy"` +
            ` alt="${cc.toUpperCase()}">`;
    }

    // A guest's finished score buys them a hypothetical standing on the board
    // for the puzzle they just solved (window.lexagonGuestScore, set by
    // endOfGame).
    function guestScoreFor(tab) {
        return (tab === 'puzzle' && !window.isLoggedIn && window.lexagonGuestScore != null) ?
            window.lexagonGuestScore : null;
    }

    // Part of the cache key: opening the flyout before and after solving are
    // genuinely different responses, and so are two different puzzles' boards.
    // load() reads it too, so its "show the loading state?" check stays in step
    // with what fetchBoard will hit.
    function cacheKey(tab, puzzlePostId) {
        const score = guestScoreFor(tab);
        const id = puzzlePostId ?? boardPostId(tab);
        return tab + (id ? ':' + id : '') + (score != null ? '#' + score : '');
    }

    // puzzlePostId is optional — omitted, the 'puzzle' tab resolves it from the
    // page, so the endscreen teaser and the flyout land on the same board (and
    // therefore share one cached response instead of firing two requests).
    async function fetchBoard(tab, puzzlePostId) {
        puzzlePostId = puzzlePostId ?? boardPostId(tab);
        const guestScore = guestScoreFor(tab);
        const key = cacheKey(tab, puzzlePostId);
        const hit = cache[key];
        if (hit && Date.now() - hit.at < CACHE_MS) return hit.data;

        const body = new URLSearchParams({
            action: ACTIONS[tab],
            nonce: window.lexagonPublicNonce || '',
        });
        if (puzzlePostId) body.set('puzzle_post_id', puzzlePostId);
        if (guestScore != null) body.set('guest_score', guestScore);

        const res = await fetch(AJAX_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body,
        });
        const json = await res.json();
        if (!json || !json.success) throw new Error('leaderboard fetch failed');
        cache[key] = { at: Date.now(), data: json.data };
        return json.data;
    }

    // Podium hooks on .lb-rank — mirrors lexagon_lb_rank_class() in
    // leaderboard-functions.php, which the leaderboard page uses
    const RANK_CLASS = { 1: ' first-place', 2: ' second-place', 3: ' third-place' };

    function rowHTML(row, meId) {
        const isMe = meId && row.userId === meId;
        return `<li class="lb-row${isMe ? ' lb-me' : ''}">` +
            `<span class="lb-rank${RANK_CLASS[row.rank] || ''}">${row.rank}</span>` +
            `<a class="lb-player" href="/player/${row.userId}">` +
            `<img class="lb-avatar" src="${escapeHTML(row.avatar)}" alt="" loading="lazy">` +
            `<span class="lb-name">${escapeHTML(row.name)}</span>` +
            (row.country ? flagImg(row.country) : '') +
            `</a>` +
            `<span class="lb-points">${(row.points || 0).toLocaleString()}</span>` +
            `</li>`;
    }

    // The guest's "you'd be here" pill — a whole row that is itself the
    // sign-up link, sitting at the rank their score would have earned.
    function ghostRowHTML(guest) {
        return `<li class="lb-row lb-ghost">` +
            `<span class="lb-rank">${guest.rank}</span>` +
            `<a class="lb-ghost-cta" href="/login">` +
            `<span class="lb-ghost-copy">Create a free account to appear on the leaderboard</span>` +
            `<span class="lb-ghost-chevron">›</span>` +
            `</a>` +
            `<span class="lb-points">${(guest.score || 0).toLocaleString()}</span>` +
            `</li>`;
    }

    function render(tab, data) {
        if (!flyout) return;
        const bodyEl = flyout.querySelector('.lb-body');
        const headlineEl = flyout.querySelector('.lb-week-headline');
        const pinnedEl = flyout.querySelector('.lb-you-pinned');
        const meId = data.you ? data.you.userId : 0;

        // Headline: your standing on this board. The puzzle tab names which
        // puzzle it is — "Today" only when that's actually what you're playing.
        let headline = '';
        if (data.you) {
            const label = tab === 'weekly' ? 'This week' :
                tab === 'alltime' ? 'All-time' :
                data.isToday ? 'Today' :
                data.puzzleNumber ? `Puzzle #${escapeHTML(data.puzzleNumber)}` : 'This puzzle';
            headline = `${label}: <strong>#${data.you.rank}</strong> · ${data.you.points.toLocaleString()} pts`;
            if (tab === 'puzzle' && data.you.passed > 0) {
                headline += ` · passed ${data.you.passed} player${data.you.passed === 1 ? '' : 's'}`;
            }
        } else if (!window.isLoggedIn) {
            headline = tab === 'weekly' ?
                'Create an account to appear on the Leaderboard' : '';
        }
        headlineEl.innerHTML = headline;

        // A guest who just solved gets slotted into the lineup where their
        // score would land. The surrounding rows keep their real ranks — the
        // guest isn't actually on the board, so renumbering would be a lie.
        const ghostAt = (!window.isLoggedIn && data.guest &&
            data.guest.rank <= data.top.length + 1) ? data.guest.rank - 1 : -1;

        // Last week's champions ride above this week's board, so a freshly
        // reset weekly tab still shows who won rather than looking wiped
        const MEDALS = { 1: '🥇', 2: '🥈', 3: '🥉' };
        const champs = (tab === 'weekly' && Array.isArray(data.lastWeek)) ? data.lastWeek : [];
        const champsHTML = champs.length ?
            '<div class="lb-champs"><div class="lb-champs-title">Last week\'s winners</div>' +
            champs.map(c =>
                `<a class="lb-champ" href="/player/${c.userId}">` +
                `<span class="lb-champ-medal">${MEDALS[c.rank] || ''}</span>` +
                `<img class="lb-avatar" src="${escapeHTML(c.avatar)}" alt="" loading="lazy">` +
                `<span class="lb-name">${escapeHTML(c.name)}</span>` +
                `<span class="lb-points">${(c.points || 0).toLocaleString()}</span>` +
                `</a>`).join('') +
            '</div>' : '';

        if (!data.top.length && ghostAt < 0) {
            bodyEl.innerHTML = champsHTML +
                '<div class="lb-empty">No solvers yet — be the first!</div>';
        } else {
            const rows = data.top.map(r => rowHTML(r, meId));
            if (ghostAt >= 0) rows.splice(ghostAt, 0, ghostRowHTML(data.guest));
            bodyEl.innerHTML = champsHTML + '<ol class="lb-list">' + rows.join('') + '</ol>';
        }

        // Guest CTA / pinned own row when ranked below the last visible row.
        // The inline ghost pill already carries the CTA when it's on screen.
        if (!window.isLoggedIn) {
            pinnedEl.innerHTML = ghostAt >= 0 ? '' :
                '<a class="lb-row lb-cta" href="/login">Create a free account to be ranked →</a>';
        } else if (data.you && data.you.rank > data.top.length) {
            pinnedEl.innerHTML = '<ol class="lb-list">' + rowHTML(data.you, meId) + '</ol>';
        } else {
            pinnedEl.innerHTML = '';
        }

        // Centre your own row — or the guest's ghost pill — in the list.
        // Deliberately NOT scrollIntoView(): that walks every scrollable
        // ancestor up to the document, so it scrolls the whole game page to
        // bring the row into view. Setting scrollTop moves this container and
        // nothing else. Rect deltas rather than offsetTop so it doesn't depend
        // on which ancestor is the offsetParent.
        const me = bodyEl.querySelector('.lb-me, .lb-ghost');
        if (me) {
            const delta = me.getBoundingClientRect().top - bodyEl.getBoundingClientRect().top;
            bodyEl.scrollTop += delta - (bodyEl.clientHeight - me.offsetHeight) / 2;
        }
    }

    let currentTab = 'puzzle';

    async function load(tab) {
        if (!flyout) return;
        currentTab = tab;
        flyout.querySelectorAll('.lb-tab').forEach(btn =>
            btn.classList.toggle('active', btn.dataset.tab === tab));
        const bodyEl = flyout.querySelector('.lb-body');
        const cached = cache[cacheKey(tab)];
        if (!cached || Date.now() - cached.at >= CACHE_MS) {
            bodyEl.innerHTML = '<div class="lb-loading">Loading…</div>';
        }
        try {
            render(tab, await fetchBoard(tab));
        } catch (e) {
            if (currentTab === tab) {
                bodyEl.innerHTML = '<div class="lb-empty">Couldn’t load the board — try again shortly.</div>';
            }
        }
    }

    flyout?.querySelectorAll('.lb-tab').forEach(btn => {
        btn.addEventListener('click', () => {
            if (typeof LexagonAudio !== 'undefined') LexagonAudio.play('button');
            load(btn.dataset.tab);
        });
    });

    document.addEventListener('lexagon:lb-open', (e) => {
        load(e.detail?.tab || currentTab);
    });

    window.LexagonBoards = { fetchBoard, flagImg };
})();
